/**
 * "Sellable" (AD-672 S3, `q-shard-faucet-vendor-sell`) — the pure half of the
 * NPC panel's sell row. From the player's own inventory snapshot it lists every
 * bag instance a vendor might buy: in the bag (never worn, stored or listed),
 * and not a def on this vendor's own shelf (an NPC never buys what it sells —
 * trading.md §5.2). Sorted by name, then id so the list is stable.
 *
 * It decides NOTHING the server does not re-check: no price is shown or
 * computed here (the payout is `baseValue` × a server-config rate the client
 * does not hold), and ownership, location, `tradeable`, the A1 gate and the
 * daily cap are all enforced by the commit. The toast after a sale names the
 * payout. Pure; node-testable.
 */

import type { CapacityKey } from '@great-cloud/shared/client';

import { capacityRefusalText } from './capacity.js';

/** The `inventorySnapshot.items` fields this reads. */
export interface SellItemView {
  id: string;
  contentId: string;
  name: string;
  location: string;
}

export interface SellableItem {
  id: string;
  name: string;
}

export function sellableItems(
  items: readonly SellItemView[],
  shelfItemIds: readonly string[],
): SellableItem[] {
  const stocked = new Set(shelfItemIds);
  return items
    .filter((it) => it.location === 'inventory' && !stocked.has(it.contentId))
    .map((it) => ({ id: it.id, name: it.name }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

/** The toast line for a `vendorSellResult` refusal (the typed reason, in words). */
export function describeSellDenial(
  denied: string,
  capRemaining?: number,
  cap?: CapacityKey,
): string {
  switch (denied) {
    case 'capacityExceeded':
      return capacityRefusalText(cap ?? 'purse');
    case 'dailyCapReached':
      return capRemaining !== undefined && capRemaining > 0
        ? `Vendors will pay you only ◈ ${capRemaining} more today.`
        : 'Vendors will buy nothing more from you today.';
    case 'notSellable':
      return 'No vendor will buy that.';
    case 'vendorStocked':
      return 'This vendor sells that — it will not buy it back.';
    case 'itemNotFound':
      return 'That item is not in your bag.';
    case 'notUnlocked':
      return 'Vendors will not buy from you until you have found your feet (play and quest a little longer).';
    case 'gateUnavailable':
      return 'The vendor cannot check your standing right now — try again.';
    case 'reputationLocked':
      return 'This vendor will not deal with you yet.';
    default:
      return 'This NPC does not buy.';
  }
}
